import { Divider, CurecallButton, CurecallPasswordInput, ECurecallSize } from '@honestica/curecall-ui'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Link from 'src/components/navigation/link.component'
import { ChangeEvent, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { twMerge } from 'src/utils/tw-merge.util'
import EmailField from 'src/components/email-field/email-field.component'
import { getRedirectAfterLoginPath, useSession } from 'src/providers/auth.provider'
import { TTwoFactorMethod } from 'src/types/two-factor'
import TwoFactorVerification from './_components/two-factor-verification.component'
import { LifenConnectNotice } from './_components/lifen-connect-notice.component'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { TLoginCredentials } from 'src/types/session'
import { useNotification } from 'src/providers/notification.provider'
import useNotificationContent from 'src/pages/auth/_components/auth-notifications.util'
import { API_ERROR_MESSAGE, ERedirectReason, TRedirectReason } from 'src/pages/auth/_components/auth.type'
import { useLoginMethod } from 'src/hooks/use-login-method'
import { useLifenConnectLogin } from 'src/hooks/use-lifen-connect-login'
import { EAuthMethod } from 'src/types/login-method'

const LIFEN_CONNECT_ERROR_PARAM = 'error'
const LIFEN_CONNECT_ERROR_PREFIX = 'lifen_connect_'
const LIFEN_CONNECT_DENIED_ERROR = 'lifen_connect_denied'

type TTwoFactorChallenge = {
  twoFactorMethod: TTwoFactorMethod
  twoFactorToken: string
}

export const LoginPage = () => {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const location = useLocation()

  const methods = useForm<TLoginCredentials & { validationError?: unknown }>()
  const {
    formState: { errors },
    setError,
    setValue,
    register,
    clearErrors,
    watch,
    formState: { isSubmitting },
  } = methods

  const loginMethod = useLoginMethod(watch('email'))
  const isOidc = loginMethod === EAuthMethod.OIDC
  const loginWithLifenConnect = useLifenConnectLogin()
  const lifenConnectError = new URLSearchParams(location.search).get(LIFEN_CONNECT_ERROR_PARAM)

  const { login } = useSession()
  const { notify } = useNotification()
  const NOTIFICATION_CONTENT = useNotificationContent()

  const redirectTo = (reason: TRedirectReason, email?: string) => {
    navigate(`/auth/password-form?redirectReason=${reason}&email=${email ? encodeURIComponent(email) : ''}`)
  }

  const [twoFactorChallenge, setTwoFactorChallenge] = useState<TTwoFactorChallenge | null>(null)

  const postLogin = async () => {
    const redirectPath = getRedirectAfterLoginPath(location.search)
    navigate(redirectPath ?? '/auth/select-project')
  }

  const onSubmit = async (data: TLoginCredentials) => {
    if (isOidc) {
      loginWithLifenConnect(data.email)
      return
    }
    try {
      if (!login) return
      const result = await login(data)

      if (result.requires2fa) {
        setTwoFactorChallenge({
          twoFactorMethod: result.twoFactorMethod,
          twoFactorToken: result.twoFactorToken,
        })
        return
      }

      await postLogin()
    } catch (e) {
      const error = (e as Error).message
      if (error?.includes(API_ERROR_MESSAGE.VALIDATE_EMAIL_INIT_PASSWORD)) {
        notify(NOTIFICATION_CONTENT.VALIDATE_EMAIL_INIT_PASSWORD.WARNING, 10000)
        redirectTo(ERedirectReason.VALIDATE_EMAIL_INIT_PASSWORD, data.email)
      } else if (error?.includes(API_ERROR_MESSAGE.VALIDATE_EMAIL)) {
        notify(NOTIFICATION_CONTENT.VALIDATE_EMAIL.WARNING, 10000)
        redirectTo(ERedirectReason.VALIDATE_EMAIL, data.email)
      } else if (error?.includes('Unauthorized')) {
        setError('validationError', { type: 'custom', message: t('pages.auth.error.email') })
      } else {
        setError('validationError', { type: 'custom', message: t('pages.auth.error.server') })
      }
    }
  }

  const handleOnChangePassword = (event: ChangeEvent<HTMLInputElement>) => {
    setValue('password', event.target.value)
    clearErrors('password')
    clearErrors('validationError')
  }

  const lifenConnectErrorMessage = lifenConnectError?.startsWith(LIFEN_CONNECT_ERROR_PREFIX)
    ? lifenConnectError === LIFEN_CONNECT_DENIED_ERROR
      ? t('pages.auth.error.lifen-connect-denied')
      : t('pages.auth.error.lifen-connect')
    : undefined

  if (twoFactorChallenge) {
    return (
      <div className="c-flex c-flex-col c-overflow-auto">
        <TwoFactorVerification
          twoFactorMethod={twoFactorChallenge.twoFactorMethod}
          twoFactorToken={twoFactorChallenge.twoFactorToken}
          onSuccess={postLogin}
          onCancel={() => setTwoFactorChallenge(null)}
        />
      </div>
    )
  }

  return (
    <div className="c-flex c-flex-col c-overflow-auto">
      <section className="c-flex c-flex-col c-gap-8 c-items-center">
        <h2 className="c-font-semibold">{t('pages.auth.title.connexion')}</h2>
        <FormProvider {...methods}>
          <form
            method="POST"
            action="#"
            onSubmit={methods.handleSubmit(onSubmit)}
            className="c-w-full c-flex c-flex-col c-gap-10 c-items-center [&>*]:c-w-3/4"
          >
            {lifenConnectErrorMessage && (
              <p role="alert" className="c-text-sm c-text-danger-500 c-font-nexa c-m-0">
                {lifenConnectErrorMessage}
              </p>
            )}
            <EmailField name="email" requiredMessageError={t('pages.auth.error.email-required')} />
            {isOidc ? (
              <LifenConnectNotice />
            ) : (
              <div className="c-flex c-flex-col">
                <CurecallPasswordInput
                  {...register('password', { required: t('pages.auth.error.password-required') })}
                  label={t('pages.auth.input.password')}
                  placeholder={t('pages.auth.input.password')}
                  isRequired={true}
                  errorMessage={errors?.password?.message || (errors?.validationError && ' ')}
                  onChange={handleOnChangePassword}
                />
                <Link
                  href={'/auth/forgot-password'}
                  className={twMerge(
                    'c-block c-self-end c-mt-2 c-max-w-max c-font-medium c-text-primary hover:c-text-primary-700',
                  )}
                >
                  {t('pages.auth.cta.forgot-password')}
                </Link>
              </div>
            )}
            {errors?.validationError?.message && (
              <div>
                <p className="c-text-base c-tracking-tight c-text-danger-500 c-font-nexa c-m-0 c-mb-1.5">
                  {t('pages.auth.error.credentials')}
                </p>
                <p className="c-text-sm c-tracking-tight c-text-danger-500 c-font-nexa c-m-0">
                  {errors?.validationError?.message}
                </p>
              </div>
            )}
            <CurecallButton
              isLoading={isSubmitting}
              type="submit"
              size={ECurecallSize.LARGE}
              className="c-mb-auto"
            >
              {isOidc ? t('pages.auth.cta.continue-with-lifen') : t('pages.auth.cta.connexion-button-1')}
            </CurecallButton>
          </form>
        </FormProvider>
      </section>

      <div className="c-mt-12 c-space-y-5">
        <Divider label={t('pages.auth.wording.or')} />
        <div className="c-flex c-aligns-center c-justify-center c-space-x-5">
          <h2 className="c-text-center c-font-nexa c-text-sm c-text-gray-800">
            {t('pages.auth.description.connexion-description')}
          </h2>
          <a
            href="https://calendar.app.google/s2EMmzvSkHhvmpiT7"
            className="c-text-primary hover:c-text-primary-700"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('pages.auth.cta.connexion-button-2')}
          </a>
        </div>
      </div>
    </div>
  )
}
